// The camera reads (TOM-797, api_contracts.md §5.24): the camera list, the
// "Seen by Neomaris since connected" event list, and the snapshot an alarm was
// raised for. Small hooks over `api()`, as useEngineSummary is.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import {
  camerasPath,
  cameraEventsQuery,
  isCameraAlarmPath,
  type CameraEvent,
  type CameraEventFilter,
  type CameraEventsResponse,
  type CameraSnapshotRef,
  type CamerasResponse,
} from './cameras';

/** The list refreshes this often while the page is visible. */
export const CAMERAS_POLL_MS = 30_000;

export type CamerasState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: CamerasResponse };

function visible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** The boat's cameras, read again every 30 s while the page is visible. */
export function useCameras(boatId: string): { state: CamerasState; retry: () => void } {
  const [state, setState] = useState<CamerasState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer: number | null = null;
    let inFlight = false;
    async function load() {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await api<CamerasResponse>(camerasPath(boatId));
        if (alive) setState({ kind: 'ready', data });
      } catch {
        // A failed refresh keeps the list it has; only a first read shows the error.
        if (alive) setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
      } finally {
        inFlight = false;
      }
    }
    function schedule() {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        if (!alive) return;
        if (visible()) void load();
        schedule();
      }, CAMERAS_POLL_MS);
    }
    function onVisibility() {
      if (visible()) {
        void load();
        schedule();
      }
    }
    setState((prev) => (prev.kind === 'error' ? { kind: 'loading' } : prev));
    void load();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [boatId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}

/**
 * Whether the boat has a camera, for the sidebar's Cameras row (Mike,
 * 2026-10-19): read once per boat. Null until the first read answers; a
 * failed read says no. A null boat id reads nothing and answers null: a Try it
 * guest has no Cameras row (TOM-868).
 */
export function useBoatHasCameras(boatId: string | null): boolean | null {
  const [has, setHas] = useState<{ boatId: string; has: boolean } | null>(null);
  useEffect(() => {
    if (boatId === null) return;
    let alive = true;
    void api<CamerasResponse>(camerasPath(boatId)).then(
      (data) => {
        if (alive) setHas({ boatId, has: (data?.cameras?.length ?? 0) > 0 });
      },
      () => {
        if (alive) setHas({ boatId, has: false });
      }
    );
    return () => {
      alive = false;
    };
  }, [boatId]);
  return boatId !== null && has !== null && has.boatId === boatId ? has.has : null;
}

export type CameraEventsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      label: string;
      events: CameraEvent[];
      nextBefore: string | null;
      loadingMore: boolean;
      moreFailed: boolean;
    };

/** The event list for a filter, newest first, with "Load more" by `nextBefore`. */
export function useCameraEvents(
  boatId: string,
  filter: CameraEventFilter
): { state: CameraEventsState; loadMore: () => void; retry: () => void } {
  const [state, setState] = useState<CameraEventsState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const typesKey = filter.types.join(',');
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    setState({ kind: 'loading' });
    const query = cameraEventsQuery({ cameraId: filter.cameraId, types: filter.types }, null);
    void api<CameraEventsResponse>(`${camerasPath(boatId)}/events?${query}`).then(
      (res) => {
        if (generation.current !== gen) return;
        setState({
          kind: 'ready',
          label: res.label,
          events: res.events,
          nextBefore: res.nextBefore,
          loadingMore: false,
          moreFailed: false,
        });
      },
      () => {
        if (generation.current !== gen) return;
        setState({ kind: 'error' });
      }
    );
    // filter.types is keyed by its joined value, so a new array of the same
    // types does not read the list again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boatId, filter.cameraId, typesKey, attempt]);

  const loadMore = useCallback(() => {
    if (state.kind !== 'ready' || state.nextBefore === null || state.loadingMore) return;
    const gen = generation.current;
    const query = cameraEventsQuery(
      { cameraId: filter.cameraId, types: filter.types },
      state.nextBefore
    );
    setState({ ...state, loadingMore: true, moreFailed: false });
    void api<CameraEventsResponse>(`${camerasPath(boatId)}/events?${query}`).then(
      (res) => {
        if (generation.current !== gen) return;
        setState((prev) => {
          if (prev.kind !== 'ready') return prev;
          const seen = new Set(prev.events.map((e) => e.eventId));
          return {
            ...prev,
            events: [...prev.events, ...res.events.filter((e) => !seen.has(e.eventId))],
            nextBefore: res.nextBefore,
            loadingMore: false,
          };
        });
      },
      () => {
        if (generation.current !== gen) return;
        setState((prev) =>
          prev.kind === 'ready' ? { ...prev, loadingMore: false, moreFailed: true } : prev
        );
      }
    );
  }, [boatId, filter.cameraId, filter.types, state]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, loadMore, retry };
}

/** One alarm whose snapshot is asked for. */
export interface CameraAlarmRequest {
  alarmId: string;
  path: string;
  /** The alarm's last transition, ISO. */
  at: string;
}

/** A freshly raised alarm's snapshot may still be on the way: look once more. */
export const CAMERA_ALARM_RECHECK_MS = 20_000;
const FRESH_MS = 5 * 60_000;

/**
 * The snapshot each camera alarm was raised for, by alarm id: read once per
 * alarm and time, and once more after 20 s for an alarm raised just now. An
 * alarm with no snapshot is left out, so its screen shows nothing for it.
 */
export function useCameraAlarmSnapshots(
  boatId: string,
  requests: readonly CameraAlarmRequest[]
): ReadonlyMap<string, CameraSnapshotRef> {
  const [found, setFound] = useState<ReadonlyMap<string, CameraSnapshotRef>>(new Map());
  const asked = useRef(new Set<string>());
  const timers = useRef(new Set<number>());
  const alive = useRef(true);

  const wanted = useMemo(
    () => requests.filter((r) => isCameraAlarmPath(r.path) && Number.isFinite(Date.parse(r.at))),
    [requests]
  );
  const wantedKey = wanted.map((r) => `${r.alarmId}|${r.path}|${r.at}`).join('\n');

  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      for (const t of pending) window.clearTimeout(t);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    for (const r of wanted) {
      const key = `${boatId}|${r.alarmId}|${r.path}|${r.at}`;
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      const read = async (recheck: boolean) => {
        let snapshot: CameraSnapshotRef | null = null;
        try {
          const q = new URLSearchParams({ path: r.path, at: new Date(r.at).toISOString() });
          const res = await api<{ snapshot: CameraSnapshotRef | null }>(
            `${camerasPath(boatId)}/alarm-snapshot?${q.toString()}`
          );
          snapshot = res.snapshot ?? null;
        } catch {
          snapshot = null;
        }
        if (!alive.current) return;
        if (snapshot !== null) {
          const shot = snapshot;
          setFound((prev) => new Map(prev).set(r.alarmId, shot));
          return;
        }
        if (!recheck && Date.now() - Date.parse(r.at) < FRESH_MS) {
          const t = window.setTimeout(() => {
            timers.current.delete(t);
            void read(true);
          }, CAMERA_ALARM_RECHECK_MS);
          timers.current.add(t);
        }
      };
      void read(false);
    }
    // Keyed by the requests' joined value, not the array's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boatId, wantedKey]);

  return found;
}
